import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import referralService from '../services/referralService';
import './styles/Referral.css';

const Referral = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [referralData, setReferralData] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [applyCodeInput, setApplyCodeInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('bearerToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchInitialData();
  }, [navigate]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchReferralCode(),
        fetchStatistics(),
        fetchLeaderboard()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferralCode = async () => {
    try {
      const response = await referralService.getUserReferralCode();
      setReferralData(response.data);
    } catch (err) {
      console.error('Error fetching referral code:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await referralService.getDetailedStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const fetchReferralHistory = async (page = 1) => {
    try {
      setLoading(true);
      const response = await referralService.getReferralHistory(page, 10);
      setReferralHistory(response.data.data);
      setCurrentPage(response.data.current_page);
      setTotalPages(response.data.last_page);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await referralService.getLeaderboard(10);
      setLeaderboard(response.data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  };

  const handleGenerateNewCode = async () => {
    setGenerating(true);
    try {
      const response = await referralService.generateNewReferralCode();
      setReferralData(response.data);
      setSuccessMessage('New referral code generated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralData?.referral_code) return;
    
    try {
      await referralService.copyReferralLink(referralData.referral_code);
      setSuccessMessage('Referral link copied to clipboard!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to copy referral link');
    }
  };

  const handleShareReferralLink = async () => {
    if (!referralData?.referral_code) return;
    
    try {
      const shared = await referralService.shareReferralLink(
        referralData.referral_code, 
        referralData.user_name
      );
      if (!shared) {
        setSuccessMessage('Referral link copied to clipboard!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError('Failed to share referral link');
    }
  };

  const handleApplyReferralCode = async () => {
    if (!applyCodeInput.trim()) return;
    
    try {
      const response = await referralService.applyReferralCode(applyCodeInput.trim());
      setSuccessMessage(response.message);
      setApplyCodeInput('');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh data
      await fetchInitialData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReferralLink = () => {
    if (!referralData?.referral_code) return '';
    return `${window.location.origin}/signup?ref=${referralData.referral_code}`;
  };

  if (loading && !referralData) {
    return (
      <div className="referral-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading referral data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-container">
      <div className="referral-content">
        {/* Header */}
        <div className="referral-header">
          <div className="header-content">
            <h1>Referral Program</h1>
            <p>Earn rewards by inviting friends to join RoamBis</p>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <i className="fas fa-users"></i>
              <div className="stat-info">
                <span className="stat-value">{referralData?.statistics?.total_referrals || 0}</span>
                <span className="stat-label">Total Referrals</span>
              </div>
            </div>
            <div className="stat-card">
              <i className="fas fa-dollar-sign"></i>
              <div className="stat-info">
                <span className="stat-value">{formatCurrency(referralData?.statistics?.total_earnings || 0)}</span>
                <span className="stat-label">Total Earnings</span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="message success-message">
            <i className="fas fa-check-circle"></i>
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="message error-message">
            <i className="fas fa-exclamation-circle"></i>
            {error}
            <button onClick={() => setError(null)} className="close-message">×</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-chart-pie"></i>
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('history');
              if (referralHistory.length === 0) {
                fetchReferralHistory();
              }
            }}
          >
            <i className="fas fa-history"></i>
            History
          </button>
          <button 
            className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            <i className="fas fa-trophy"></i>
            Leaderboard
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* Referral Code Section */}
              <div className="referral-code-section">
                <div className="section-header">
                  <h3>Your Referral Code</h3>
                  <p>Share your code with friends and earn rewards on their purchases</p>
                </div>
                
                <div className="referral-code-card">
                  <div className="code-display">
                    <span className="code-label">Referral Code:</span>
                    <span className="code-value">{referralData?.referral_code || 'Loading...'}</span>
                  </div>
                  
                  <div className="code-actions">
                    <button 
                      className="action-button copy-button"
                      onClick={handleCopyReferralLink}
                      disabled={!referralData?.referral_code}
                    >
                      <i className="fas fa-copy"></i>
                      Copy Link
                    </button>
                    
                    <button 
                      className="action-button share-button"
                      onClick={handleShareReferralLink}
                      disabled={!referralData?.referral_code}
                    >
                      <i className="fas fa-share-alt"></i>
                      Share
                    </button>
                    
                    <button 
                      className="action-button generate-button"
                      onClick={handleGenerateNewCode}
                      disabled={generating}
                    >
                      <i className={`fas ${generating ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                      {generating ? 'Generating...' : 'New Code'}
                    </button>
                  </div>
                </div>

                <div className="referral-link-display">
                  <label>Referral Link:</label>
                  <div className="link-container">
                    <input 
                      type="text" 
                      readOnly 
                      value={getReferralLink()}
                      className="link-input"
                    />
                    <button 
                      className="copy-link-button"
                      onClick={handleCopyReferralLink}
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Apply Referral Code Section */}
              {!statistics?.personal?.referred_by && (
                <div className="apply-code-section">
                  <div className="section-header">
                    <h3>Have a Referral Code?</h3>
                    <p>Enter a friend's referral code to connect with them</p>
                  </div>
                  
                  <div className="apply-code-card">
                    <div className="input-group">
                      <input
                        type="text"
                        placeholder="Enter referral code..."
                        value={applyCodeInput}
                        onChange={(e) => setApplyCodeInput(e.target.value)}
                        className="code-input"
                      />
                      <button 
                        className="apply-button"
                        onClick={handleApplyReferralCode}
                        disabled={!applyCodeInput.trim()}
                      >
                        Apply Code
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Statistics Section */}
              <div className="statistics-section">
                <div className="section-header">
                  <h3>Your Statistics</h3>
                </div>
                
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-icon pending">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="stat-details">
                      <span className="stat-number">{referralData?.statistics?.pending_referrals || 0}</span>
                      <span className="stat-title">Pending Referrals</span>
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-icon completed">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="stat-details">
                      <span className="stat-number">{referralData?.statistics?.completed_referrals || 0}</span>
                      <span className="stat-title">Completed Referrals</span>
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-icon earnings">
                      <i className="fas fa-money-bill-wave"></i>
                    </div>
                    <div className="stat-details">
                      <span className="stat-number">{formatCurrency(referralData?.statistics?.total_earnings || 0)}</span>
                      <span className="stat-title">Total Earnings</span>
                    </div>
                  </div>
                  
                  <div className="stat-item">
                    <div className="stat-icon rate">
                      <i className="fas fa-star"></i>
                    </div>
                    <div className="stat-details">
                      <span className="stat-number">Active</span>
                      <span className="stat-title">Referral Status</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Referred By Section */}
              {statistics?.personal?.referred_by && (
                <div className="referred-by-section">
                  <div className="section-header">
                    <h3>You Were Referred By</h3>
                  </div>
                  
                  <div className="referred-by-card">
                    <div className="referrer-info">
                      <div className="referrer-avatar">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="referrer-details">
                        <span className="referrer-name">{statistics.personal.referred_by.name}</span>
                        <span className="referrer-code">Code: {statistics.personal.referred_by.referral_code}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="history-tab">
              <div className="section-header">
                <h3>Referral History</h3>
                <p>Track your referral progress and earnings</p>
              </div>
              
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading history...</p>
                </div>
              ) : referralHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-users"></i>
                  <h4>No Referrals Yet</h4>
                  <p>Start sharing your referral code to see your referrals here!</p>
                </div>
              ) : (
                <>
                  <div className="history-list">
                    {referralHistory.map((referral) => (
                      <div key={referral.id} className="history-item">
                        <div className="referral-info">
                          <div className="user-avatar">
                            <i className="fas fa-user"></i>
                          </div>
                          <div className="user-details">
                            <span className="user-name">{referral.referred.name}</span>
                            <span className="user-email">{referral.referred.email}</span>
                            <span className="referral-date">Joined: {formatDate(referral.created_at)}</span>
                          </div>
                        </div>
                        
                        <div className="referral-status">
                          <span className={`status-badge ${referral.status}`}>
                            {referral.status === 'completed' && <i className="fas fa-check-circle"></i>}
                            {referral.status === 'pending' && <i className="fas fa-clock"></i>}
                            {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                          </span>
                        </div>
                        
                        <div className="referral-earnings">
                          {referral.status === 'completed' ? (
                            <span className="earnings-amount">
                              {formatCurrency(referral.commission_amount)}
                            </span>
                          ) : (
                            <span className="earnings-pending">Pending</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-button"
                        onClick={() => fetchReferralHistory(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button 
                        className="pagination-button"
                        onClick={() => fetchReferralHistory(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="leaderboard-tab">
              <div className="section-header">
                <h3>Top Referrers</h3>
                <p>See who's leading the referral game</p>
              </div>
              
              {leaderboard.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-trophy"></i>
                  <h4>No Leaderboard Data</h4>
                  <p>Be the first to start referring friends!</p>
                </div>
              ) : (
                <div className="leaderboard-list">
                  {leaderboard.map((user, index) => (
                    <div key={user.id} className={`leaderboard-item ${index < 3 ? 'top-three' : ''}`}>
                      <div className="rank">
                        {index === 0 && <i className="fas fa-crown gold"></i>}
                        {index === 1 && <i className="fas fa-crown silver"></i>}
                        {index === 2 && <i className="fas fa-crown bronze"></i>}
                        {index > 2 && <span className="rank-number">#{index + 1}</span>}
                      </div>
                      
                      <div className="user-info">
                        <img 
                          src={user.avatar} 
                          alt={user.name}
                          className="user-avatar"
                        />
                        <div className="user-details">
                          <span className="user-name">{user.name}</span>
                          <span className="referral-code">Code: {user.referral_code}</span>
                        </div>
                      </div>
                      
                      <div className="user-stats">
                        <div className="stat">
                          <span className="stat-value">{user.total_referrals}</span>
                          <span className="stat-label">Referrals</span>
                        </div>
                        <div className="stat">
                          <span className="stat-value">{formatCurrency(user.total_earnings)}</span>
                          <span className="stat-label">Earnings</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* How It Works Section */}
        <div className="how-it-works-section">
          <div className="section-header">
            <h3>How It Works</h3>
          </div>
          
          <div className="steps-grid">
            <div className="step-item">
              <div className="step-icon">
                <i className="fas fa-share-alt"></i>
              </div>
              <div className="step-content">
                <h4>1. Share Your Code</h4>
                <p>Send your unique referral code to friends and family</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-icon">
                <i className="fas fa-user-plus"></i>
              </div>
              <div className="step-content">
                <h4>2. Friend Signs Up</h4>
                <p>They create an account using your referral code</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-icon">
                <i className="fas fa-shopping-cart"></i>
              </div>
              <div className="step-content">
                <h4>3. First Purchase</h4>
                <p>When they make their first purchase, you earn rewards</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-icon">
                <i className="fas fa-money-bill-wave"></i>
              </div>
              <div className="step-content">
                <h4>4. Earn Rewards</h4>
                <p>Get rewards added to your account</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;